import {
  LEVEL_SURFACE_DEFAULT_HREF,
  useLevelTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.level.surface";
import { SURFACE_GRID_GAP } from "@-/dev.improve.haniverse.components.ui.surface";
import { View } from "@-/com.npmjs.react-native";
import { useMemo, useState } from "react";
import { MorphemeGlyphTileGridView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import {
  useBatchedGraphemeCardStages,
  useBatchedGraphemeTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import type { Level } from "@-/dev.improve.haniverse.lib.level";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { cn } from "@-/com.npmjs.cn";
import { getLessonLevelGridLayout } from "#get-lesson-level-grid-layout";

export type LessonLevelGlyphGridProps = {
  activeMorpheme?: string;
  /**
   * The level's cards; a pronunciation level (`2-P1`) passes its own `level.pronunciationCharacters`,
   * whose tiles then show their readings' pronunciation cards. Defaults to its characters.
   */
  characters?: readonly string[];
  className?: string;
  hanziScript?: HanziScript;
  href?: string;
  level: Level;
  /** The tallest the row, including its gutter, may be. */
  maxHeight?: number;
  /** Keeps tile navigation in the caller's stack. */
  onNavigate?: (morpheme: string) => void;
  /** Viewport width used to keep every level’s tiles the same size. */
  viewportWidth?: number;
};

/** Every card of a level in one row; the lesson surface owns horizontal scrolling. */
export function LessonLevelGlyphGrid({
  activeMorpheme,
  characters: charactersProp,
  className,
  hanziScript,
  href = LEVEL_SURFACE_DEFAULT_HREF,
  level,
  maxHeight,
  onNavigate,
  viewportWidth,
}: LessonLevelGlyphGridProps) {
  const characters = charactersProp ?? level.characters;
  const [width, setWidth] = useState(0);
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const glyphs = useMemo(
    () => characters.map((character) => getGraphemeGlyph(character, { hanziScript })),
    [characters, getGraphemeGlyph, hanziScript],
  );

  // Share one batched stage and pinyin read across every row.
  const graphemes = useMemo(() => glyphs.map((glyph) => glyph.original), [glyphs]);
  const graphemeTileData = useBatchedGraphemeTileData(graphemes);
  const getCardStage = useBatchedGraphemeCardStages(graphemes);
  // Each tile shows the card this level teaches (see `useLevelTileData`), so a character just
  // learned for its meaning stays unshaded in its pronunciation level.
  const tileData = useLevelTileData({
    getCardStage,
    level,
    part: characters === level.pronunciationCharacters ? "pronunciation" : "character",
    tileData: graphemeTileData,
  });

  // Until the width is measured, the tiles fill it at the preferred column count.
  const layout =
    (viewportWidth ?? width) > 0
      ? getLessonLevelGridLayout({ count: glyphs.length, maxHeight, width: viewportWidth ?? width })
      : null;
  const activeIndex = activeMorpheme == null ? -1 : characters.indexOf(activeMorpheme);
  return (
    <View
      className={cn("px-1", className)}
      onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
      style={viewportWidth === undefined ? undefined : { width: (layout?.gridWidth ?? 0) + 8 }}
      testID="lesson-level-grid"
    >
      <View className="self-center" style={{ width: layout?.gridWidth ?? "100%" }}>
        <MorphemeGlyphTileGridView
          availableWidth={layout?.gridWidth}
          columns={layout?.columns}
          gap={SURFACE_GRID_GAP}
          graphemeList={glyphs}
          href={href}
          onNavigate={onNavigate}
          renderTileOverlay={(_morpheme, index) =>
            activeIndex >= 0 && index > activeIndex ? (
              <View className="absolute inset-0 rounded-xs bg-surface opacity-85" />
            ) : null
          }
          tileClassName={(_morpheme, index) =>
            index === activeIndex ? "border-2 border-foreground" : undefined
          }
          tileData={tileData}
          uncolored
        />
      </View>
    </View>
  );
}
